import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  SimpleChanges
} from '@angular/core';

export type AnnotationTool = 'pen' | 'rectangle' | 'arrow';

interface DrawAction {
  type: AnnotationTool;
  color: string;
  lineWidth: number;
  /** Pen: array of {x,y} points. Rect/Arrow: [start, end] */
  points: { x: number; y: number }[];
}

@Component({
  selector: 'app-screenshot-annotator',
  templateUrl: './screenshot-annotator.component.html',
  styleUrls: ['./screenshot-annotator.component.scss']
})
export class ScreenshotAnnotatorComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('annotationCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('canvasContainer') containerRef!: ElementRef<HTMLDivElement>;

  /** Base64 PNG screenshot (without data:image/png;base64, prefix) */
  @Input() screenshotBase64: string | null = null;

  /** Emits the annotated image as base64 PNG (without prefix) whenever annotations change */
  @Output() annotatedImage = new EventEmitter<string>();

  activeTool: AnnotationTool = 'pen';
  drawColor = '#ff0000';
  lineWidth = 3;

  private ctx!: CanvasRenderingContext2D;
  private bgImage: HTMLImageElement | null = null;
  private actions: DrawAction[] = [];
  private currentAction: DrawAction | null = null;
  private isDrawing = false;

  // Canvas scaling: displayed size vs actual pixel size
  private scaleX = 1;
  private scaleY = 1;
  private resizeObserver?: ResizeObserver;

  ngAfterViewInit(): void {
    const canvas = this.canvasRef.nativeElement;
    this.ctx = canvas.getContext('2d')!;
    this.loadImage();

    // Watch container size changes to keep canvas responsive
    this.resizeObserver = new ResizeObserver(() => this.resizeCanvas());
    this.resizeObserver.observe(this.containerRef.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['screenshotBase64'] && !changes['screenshotBase64'].firstChange) {
      this.actions = [];
      this.loadImage();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  // ── Tool selection ──

  selectTool(tool: AnnotationTool): void {
    this.activeTool = tool;
  }

  undo(): void {
    if (this.actions.length === 0) return;
    this.actions.pop();
    this.redraw();
    this.emitAnnotated();
  }

  resetAnnotations(): void {
    if (this.actions.length === 0) return;
    this.actions = [];
    this.redraw();
    this.emitAnnotated();
  }

  // ── Mouse / touch events ──

  onPointerDown(event: PointerEvent): void {
    event.preventDefault();
    const canvas = this.canvasRef.nativeElement;
    canvas.setPointerCapture(event.pointerId);

    const pos = this.getCanvasPos(event);
    this.isDrawing = true;
    this.currentAction = {
      type: this.activeTool,
      color: this.drawColor,
      lineWidth: this.lineWidth,
      points: [pos]
    };
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.isDrawing || !this.currentAction) return;
    event.preventDefault();

    const pos = this.getCanvasPos(event);

    if (this.currentAction.type === 'pen') {
      this.currentAction.points.push(pos);
    } else {
      // For rect/arrow, keep only start + current end
      this.currentAction.points[1] = pos;
    }

    this.redraw();
    this.drawAction(this.currentAction);
  }

  onPointerUp(event: PointerEvent): void {
    if (!this.isDrawing || !this.currentAction) return;
    event.preventDefault();

    // Only save if there's actual movement (not just a click)
    const pts = this.currentAction.points;
    if (pts.length >= 2) {
      this.actions.push(this.currentAction);
    }

    this.currentAction = null;
    this.isDrawing = false;
    this.redraw();
    this.emitAnnotated();
  }

  // ── Rendering ──

  private loadImage(): void {
    if (!this.screenshotBase64) return;

    const img = new Image();
    img.onload = () => {
      this.bgImage = img;
      this.resizeCanvas();
    };
    img.src = `data:image/png;base64,${this.screenshotBase64}`;
  }

  private resizeCanvas(): void {
    if (!this.bgImage || !this.canvasRef) return;

    const canvas = this.canvasRef.nativeElement;
    const container = this.containerRef.nativeElement;
    const containerWidth = container.clientWidth;

    // Canvas internal resolution = actual image size (for sharp export)
    canvas.width = this.bgImage.width;
    canvas.height = this.bgImage.height;

    // Display size: always fill full container width, maintaining aspect ratio
    const displayWidth = containerWidth;
    const ratio = displayWidth / this.bgImage.width;
    const displayHeight = this.bgImage.height * ratio;

    canvas.style.width = `${displayWidth}px`;
    canvas.style.height = `${displayHeight}px`;

    this.scaleX = this.bgImage.width / displayWidth;
    this.scaleY = this.bgImage.height / displayHeight;

    this.redraw();
  }

  private redraw(): void {
    if (!this.ctx || !this.bgImage) return;
    const canvas = this.canvasRef.nativeElement;

    // Clear and draw background
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.ctx.drawImage(this.bgImage, 0, 0);

    // Redraw all saved actions
    for (const action of this.actions) {
      this.drawAction(action);
    }
  }

  private drawAction(action: DrawAction): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.strokeStyle = action.color;
    ctx.lineWidth = action.lineWidth * Math.max(this.scaleX, this.scaleY);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    switch (action.type) {
      case 'pen':
        this.drawPen(ctx, action.points);
        break;
      case 'rectangle':
        this.drawRect(ctx, action.points);
        break;
      case 'arrow':
        this.drawArrow(ctx, action.points);
        break;
    }
    ctx.restore();
  }

  private drawPen(ctx: CanvasRenderingContext2D, points: { x: number; y: number }[]): void {
    if (points.length < 2) return;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.stroke();
  }

  private drawRect(ctx: CanvasRenderingContext2D, points: { x: number; y: number }[]): void {
    if (points.length < 2) return;
    const [start, end] = points;
    ctx.beginPath();
    ctx.rect(start.x, start.y, end.x - start.x, end.y - start.y);
    ctx.stroke();
  }

  private drawArrow(ctx: CanvasRenderingContext2D, points: { x: number; y: number }[]): void {
    if (points.length < 2) return;
    const [start, end] = points;

    // Shaft
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();

    // Arrowhead
    const headLen = 15 * Math.max(this.scaleX, this.scaleY);
    const angle = Math.atan2(end.y - start.y, end.x - start.x);

    ctx.beginPath();
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(
      end.x - headLen * Math.cos(angle - Math.PI / 6),
      end.y - headLen * Math.sin(angle - Math.PI / 6)
    );
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(
      end.x - headLen * Math.cos(angle + Math.PI / 6),
      end.y - headLen * Math.sin(angle + Math.PI / 6)
    );
    ctx.stroke();
  }

  // ── Helpers ──

  private getCanvasPos(event: PointerEvent): { x: number; y: number } {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * this.scaleX,
      y: (event.clientY - rect.top) * this.scaleY
    };
  }

  private emitAnnotated(): void {
    if (!this.canvasRef) return;
    const canvas = this.canvasRef.nativeElement;
    // Export as PNG data URL, strip the prefix
    const dataUrl = canvas.toDataURL('image/png');
    const base64 = dataUrl.replace(/^data:image\/png;base64,/, '');
    this.annotatedImage.emit(base64);
  }

  get hasAnnotations(): boolean {
    return this.actions.length > 0;
  }
}
